import React, { useEffect, useState } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ResponseErrorPanel, Progress } from '@backstage/core-components';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';

// "values.yaml" tab on CaaS app components: shows the app's deployed
// values.yaml (image, env vars, ingress, DB settings) directly in the
// portal, so owners can review config before re-submitting the create
// form. Content is fetched through the backend `/github-files` proxy —
// the GitHub token never reaches the browser. fetchApi (not raw fetch)
// attaches the signed-in user's Backstage token, which the proxy requires.
const ValuesYamlPanel = () => {
  const { entity } = useEntity();
  const { fetch } = useApi(fetchApiRef);
  const [content, setContent] = useState<string>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    const location =
      entity.metadata.annotations?.['backstage.io/source-location'] ?? '';
    const m = location.match(
      /^url:https:\/\/github\.com\/([^/]+)\/([^/]+)\/tree\/([^/]+)\/(.+)$/,
    );
    if (!m) {
      // Never throw on odd locations (e.g. template-2 apps whose
      // catalog-info sits at the repo root → "tree/main/") — calm notice.
      setError(
        new Error(
          `No app folder found in "${location}" — this component does not keep a values.yaml in its source location.`,
        ),
      );
      return;
    }
    const [, owner, repo, ref, dir] = m;
    fetch(
      `/api/proxy/github-files/repos/${owner}/${repo}/contents/${dir}/values.yaml?ref=${encodeURIComponent(
        ref,
      )}`,
    )
      .then(r =>
        r.ok ? r.json() : Promise.reject(new Error(`GitHub API HTTP ${r.status}`)),
      )
      .then((j: { content?: string }) => {
        const b64 = (j.content ?? '').replace(/\n/g, '');
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        setContent(new TextDecoder().decode(bytes));
      })
      .catch((e: Error) => setError(e));
  }, [entity, fetch]);

  if (error) return <ResponseErrorPanel error={error} />;
  if (content === undefined) return <Progress />;

  return (
    <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
      <pre
        style={{
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: 4,
          padding: 16,
          fontSize: 13,
          lineHeight: 1.5,
          whiteSpace: 'pre',
        }}
      >
        {content}
      </pre>
    </div>
  );
};

export const entityValuesYamlModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    EntityContentBlueprint.make({
      params: {
        defaultPath: '/values-yaml',
        defaultTitle: 'values.yaml',
        loader: async () => <ValuesYamlPanel />,
        filterPredicate: entity => {
          // Only template-1 apps: their values.yaml lives under
          // <repo>/tree/<ref>/<cluster>/apps/<ns>/<app> in container-as-service.
          // Template-2 (akslocalapp-*) components keep catalog-info at their
          // repo root ("tree/main/") and have no values.yaml there.
          const location =
            entity.metadata.annotations?.['backstage.io/source-location'] ??
            '';
          return (
            location.includes('container-as-service') &&
            /\/tree\/[^/]+\/.+/.test(location)
          );
        },
      },
    }),
  ],
});
